import * as yup from 'yup';
import { ArrowDown, ArrowUp, Edit2, Plus, Trash2 } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GenerateEventDescriptionInput, Locale } from '__generated__/graphql';

import { ErrorBag, containsKeyPrefix, getServerErrors } from '../../../../common/helpers';
import { Event } from './helpers';
import { ImageFile, ImagesInput } from '../../../../common/Common/UploadButton';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { Translation, translationsForAttribute } from '../../../Translations/helpers';
import GetGeneratedEventDescriptionQuery from './GetGeneratedEventDescriptionQuery';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useEditTranslationsModal from '../../../Translations/useEditTranslationsModal';
import useLazyQuery from '../../../../api/useLazyQuery';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

export interface EditEventAboutFormProps {
  event: Event;
}

interface EventFactFormValue {
  id: string | null;
  title: string;
  title_translations: Translation[];
  description: string;
  description_translations: Translation[];
}

const emptyFact = (overrides: Partial<EventFactFormValue> = {}): EventFactFormValue => ({
  id: null,
  title: '',
  title_translations: [],
  description: '',
  description_translations: [],
  ...overrides,
});

const getValues = (event: Event) => ({
  photos: event.photos.map((photo) => ({ id: photo.file.id, url: photo.file.url })),
  description: event.description,
  description_translations: translationsForAttribute(event.translations, 'description'),
  usp_facts: [
    ...event.usp_facts.map((fact) => ({
      id: fact.id,
      title: fact.title,
      title_translations: translationsForAttribute(fact.translations, 'title'),
    })),
    ...event.usp_facts.length < 1 ? [emptyFact()] : [],
    ...event.usp_facts.length < 2 ? [emptyFact()] : [],
    ...event.usp_facts.length < 3 ? [emptyFact()] : [],
  ],
  whats_included_facts: event.whats_included_facts.length > 0
    ? event.whats_included_facts.map((fact) => ({
      id: fact.id,
      title: fact.title,
      title_translations: translationsForAttribute(fact.translations, 'title'),
      description: fact.description || '',
      description_translations: translationsForAttribute(fact.translations, 'description'),
    }))
    : [emptyFact()],
});

const EditEventAboutForm = ({ event }: EditEventAboutFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const form = useForm({ values: getValues(event) });
  const [formKey, setFormKey] = useState(0);
  const descriptionInputKeyRef = useRef(0);
  const uspsInputKeyRef = useRef(0);
  const whatsIncludedInputKeyCountRef = useRef(form.values.whats_included_facts.length);
  const whatsIncludedInputKeysRef = useRef(form.values.whats_included_facts.map((_, index) => index));
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingUspFacts, setGeneratingUspFacts] = useState(false);
  const [generatingWhatsIncluded, setGeneratingWhatsIncluded] = useState(false);
  const [generateEventDescription] = useLazyQuery(GetGeneratedEventDescriptionQuery);

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      about: {
        photos: form.values.photos.map(({ id }) => id),
        description: form.values.description,
        description_translations: form.values.description_translations,
        usp_facts: form.values.usp_facts.filter((value) => value.title?.trim()),
        whats_included_facts: form.values.whats_included_facts.filter((value) => value.title?.trim()),
      },
    },
    onSuccess: (updatedEvent) => {
      form.reset(getValues(updatedEvent));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: yup.object(),
    values: form.values,
    externalErrors: getServerErrors(error, 'about'),
  });

  const handleUpload = (file: ImageFile) => {
    form.set((values) => ({ ...values, photos: [...values.photos, file] }));
  };

  const [recentlyAddedWhatsIncludedFact, setRecentlyAddedWhatsIncludedFact] = useState(false);

  const addWhatsIncludedFact = () => {
    setRecentlyAddedWhatsIncludedFact(true);

    whatsIncludedInputKeyCountRef.current += 1;
    whatsIncludedInputKeysRef.current = [...whatsIncludedInputKeysRef.current, whatsIncludedInputKeyCountRef.current];
    form.set((values) => ({
      ...values,
      whats_included_facts: [...values.whats_included_facts, emptyFact()],
    }));
  };

  const deleteWhatsIncludedFact = (index: number) => {
    whatsIncludedInputKeysRef.current = whatsIncludedInputKeysRef.current.filter((_, itemIndex) => itemIndex !== index);
    form.set((values) => ({
      ...values,
      whats_included_facts: values.whats_included_facts.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const moveWhatsIncludedFact = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= form.values.whats_included_facts.length) {
      return;
    }

    const whatsIncludedInputKeys = [...whatsIncludedInputKeysRef.current];
    [whatsIncludedInputKeys[index], whatsIncludedInputKeys[targetIndex]] = [
      whatsIncludedInputKeys[targetIndex], whatsIncludedInputKeys[index],
    ];
    whatsIncludedInputKeysRef.current = whatsIncludedInputKeys;

    form.set((values) => {
      const facts = [...values.whats_included_facts];
      [facts[index], facts[targetIndex]] = [facts[targetIndex], facts[index]];

      return { ...values, whats_included_facts: facts };
    });
  };

  const generate = async (input: Partial<GenerateEventDescriptionInput>) => {
    if (input.generate_description) {
      setGeneratingDescription(true);
    }

    if (input.generate_usp_facts) {
      setGeneratingUspFacts(true);
    }

    if (input.generate_whats_included_facts) {
      setGeneratingWhatsIncluded(true);
    }

    const result = await generateEventDescription({
      variables: {
        project_id: project.id,
        input: {
          event_id: event.id,
          ...input,
        },
      },
    });

    if (input.generate_description) {
      setGeneratingDescription(false);
    }

    if (input.generate_usp_facts) {
      setGeneratingUspFacts(false);
    }

    if (input.generate_whats_included_facts) {
      setGeneratingWhatsIncluded(false);
    }

    if (!result.data) {
      return;
    }

    const generated = result.data.project.generated_event_description;

    if (generated.description) {
      descriptionInputKeyRef.current += 1;
      form.set('description', generated.description);
    }

    if (generated.usp_facts?.length > 0) {
      uspsInputKeyRef.current += 1;

      form.set((values) => ({
        ...values,
        usp_facts: values.usp_facts.map((value, index) => emptyFact({
          id: value.id,
          title: generated.usp_facts[index] || '',
        })),
      }));
    }

    if (generated.whats_included_facts?.length > 0) {
      const newFacts = generated.whats_included_facts.map((title) => emptyFact({ title }));

      form.set((values) => {
        // Drop trailing empty rows so generated facts don't leave a blank row above them.
        let keepCount = values.whats_included_facts.length;
        while (keepCount > 0 && !values.whats_included_facts[keepCount - 1].title?.trim()) {
          keepCount -= 1;
        }

        whatsIncludedInputKeysRef.current = [
          ...whatsIncludedInputKeysRef.current.slice(0, keepCount),
          ...newFacts.map(() => {
            whatsIncludedInputKeyCountRef.current += 1;
            return whatsIncludedInputKeyCountRef.current;
          }),
        ];

        return {
          ...values,
          whats_included_facts: [
            ...values.whats_included_facts.slice(0, keepCount),
            ...newFacts,
          ],
        };
      });
    }
  };

  const canGenerate = !!event.website_url;

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('about_the_event')}</UI.Legend>
          <UI.InputDescription>{t('about_the_event_description')}</UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.photos, touched: form.touched.photos }}>
          <UI.InputLabel>{t('event_photos')}</UI.InputLabel>
          <UI.InputDescription>
            {t('event_photos_description', { max: 10 })}
          </UI.InputDescription>
          <ImagesInput
            value={form.values.photos}
            onUpload={handleUpload}
            onChange={(photos) => form.set('photos', photos)}
            max={10}
          />
          <UI.ErrorMessages attribute={t('event_photos')} errors={errors.photos} />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !containsKeyPrefix(errors, 'usp_facts'), touched: form.getTouched('usp_facts') }}
          key={`usps_${uspsInputKeyRef.current}`}
        >
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.InputLabel htmlFor="event_usp_1">
              {t('event_highlights')}
            </UI.InputLabel>
            {canGenerate && (
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                disabled={generatingUspFacts}
                onClick={() => generate({ generate_usp_facts: true })}
              >
                <UI.Icon>
                  {generatingUspFacts ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                </UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            )}
          </UI.FlexContainer>
          <UI.InputDescription>
            {t('event_highlights_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            {form.values.usp_facts.map((value, index) => (
              <UI.Div key={index}>
                <TranslatableInput
                  form={form}
                  name={`usp_facts.${index}.title`}
                  errors={errors}
                  id={`event_usp_${index + 1}`}
                  title={t('event_usp_n', { n: index + 1 })}
                  defaultLocale={event.locale}
                  maxLength={50}
                  sc={{ pl: 6 }}
                  disabled={generatingUspFacts}
                  inputPrefix={<UI.Badge sc={{ outline: true }}>{index + 1}</UI.Badge>}
                />
                <UI.ErrorMessages
                  attribute={t('event_usp_n', { n: index + 1 })}
                  errors={errors[`usp_facts.${index}.title`]}
                />
              </UI.Div>
            ))}
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.description, touched: form.touched.description }}
          key={descriptionInputKeyRef.current}
        >
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.InputLabel id="EventDescriptionLabel">
              {t('event_description')}
            </UI.InputLabel>
            {canGenerate && (
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                disabled={generatingDescription}
                onClick={() => generate({ generate_description: true })}
              >
                <UI.Icon>
                  {generatingDescription
                    ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} />
                    : <UI.Icons.Ai />}
                </UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            )}
          </UI.FlexContainer>
          <UI.InputDescription>
            {t('event_description_description')}
          </UI.InputDescription>
          <TranslatableEditor
            form={form}
            errors={errors}
            name="description"
            defaultLocale={event.locale}
            title={t('event_description')}
            options={{ headings: [3] }}
            attributes={{ 'aria-labelledby': 'EventDescriptionLabel' }}
          />
          <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{
            valid: !containsKeyPrefix(errors, 'whats_included_facts'),
            touched: form.getTouched('whats_included_facts'),
          }}
        >
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.InputLabel htmlFor="event_whats_included_0">
              {t('whats_included')}
              <UI.Label sc={{ brand: 'secondary', small: true, ml: 1, fontWeight: 500 }}>
                {t('new')}
              </UI.Label>
            </UI.InputLabel>
            {canGenerate && (
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                disabled={generatingWhatsIncluded}
                onClick={() => generate({ generate_whats_included_facts: true })}
              >
                <UI.Icon>
                  {generatingWhatsIncluded
                    ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} />
                    : <UI.Icons.Ai />}
                </UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            )}
          </UI.FlexContainer>
          <UI.InputDescription>
            {t('whats_included_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            {form.values.whats_included_facts.map((fact, index) => (
              <WhatsIncludedFactInput
                form={form}
                errors={errors}
                index={index}
                defaultLocale={event.locale}
                canMoveUp={index > 0}
                canMoveDown={index < form.values.whats_included_facts.length - 1}
                key={whatsIncludedInputKeysRef.current[index]}
                onDelete={() => deleteWhatsIncludedFact(index)}
                onMoveUp={() => moveWhatsIncludedFact(index, 'up')}
                onMoveDown={() => moveWhatsIncludedFact(index, 'down')}
                autoFocus={recentlyAddedWhatsIncludedFact && index === form.values.whats_included_facts.length - 1}
              />
            ))}
            <UI.Div>
              <UI.Button onClick={addWhatsIncludedFact}>
                <UI.Icon>
                  <Plus />
                </UI.Icon>
                {' '}
                {t('add_item')}
              </UI.Button>
            </UI.Div>
          </UI.FormGrid>
          <UI.ErrorMessages attribute={t('whats_included')} errors={errors.whats_included_facts} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons form={form} submitLabel={t('save_event')} loading={loading} valid={valid} />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface WhatsIncludedFactInputProps {
  form: FormManager;
  errors: ErrorBag;
  index: number;
  defaultLocale: Locale;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  autoFocus?: boolean;
}

const WhatsIncludedFactInput = ({
  form, errors, index, defaultLocale, canMoveUp, canMoveDown, onDelete, onMoveUp, onMoveDown, autoFocus,
}: WhatsIncludedFactInputProps) => {
  const { t } = useTranslation();
  const key = (name?: string) => `whats_included_facts.${index}${name ? `.${name}` : ''}`;
  const title = form.get(key('title'));
  const description = form.get(key('description')) || '';

  const [openDescriptionModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue: description,
    translations: form.get(key('description_translations')) || [],
    inputType: 'textarea',
    title: t('add_description_to_option', { option: title }),
    onSave: (defaultValue, translations) => {
      form.set(key('description'), defaultValue || '');
      form.set(key('description_translations'), translations);
    },
  });

  return (
    <UI.InputGroup sc={{ valid: !errors[key('title')], touched: !!form.getTouched(key('title')) }}>
      <UI.FormGrid sc={{ columns: '1fr auto auto auto auto', gutter: 0.25, alignVertical: 'center' }}>
        <TranslatableInput
          form={form}
          errors={errors}
          name={key('title')}
          id={`event_whats_included_${index}`}
          defaultLocale={defaultLocale}
          title={`${t('whats_included')} ${index + 1}`}
          aria-label={`${t('whats_included')} ${index + 1}`}
          placeholder={t('whats_included_item_placeholder')}
          maxLength={30}
          autoFocus={autoFocus}
        />
        <UI.Button
          sc={{ outline: true, small: true, color: !description ? 'gray.500' : undefined }}
          onClick={openDescriptionModal}
          disabled={!title}
          title={t('add_description_to_option', { option: title || (index + 1) })}
        >
          <UI.Icon>
            {description ? <Edit2 /> : <Plus />}
          </UI.Icon>
          {' '}
          {t('description')}
        </UI.Button>
        <UI.Button
          sc={{ blank: true, size: 'sm', color: 'gray.500' }}
          onClick={onMoveUp}
          disabled={!canMoveUp}
          title={t('move_up_option', { option: title || (index + 1) })}
        >
          <UI.Icon>
            <ArrowUp />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          sc={{ blank: true, size: 'sm', color: 'gray.500' }}
          onClick={onMoveDown}
          disabled={!canMoveDown}
          title={t('move_down_option', { option: title || (index + 1) })}
        >
          <UI.Icon>
            <ArrowDown />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          sc={{ blank: true, size: 'sm', color: 'gray.500' }}
          onClick={onDelete}
          title={t('delete_option', { option: title || (index + 1) })}
        >
          <UI.Icon>
            <Trash2 />
          </UI.Icon>
        </UI.Button>
      </UI.FormGrid>
      <UI.ErrorMessages attribute={t('whats_included')} errors={errors[key('title')]} />
    </UI.InputGroup>
  );
};

export default EditEventAboutForm;
